'use strict';

/**
 * @fileoverview pluralKeys — the ONE plural-key selector for every translator outside i18next
 * (Q-R8CLOSE-2, 2026-09-25).
 *
 * THIS FILE IS THE IMPLEMENTATION; `lib/esm/pluralKeys.js` re-exports it. It is CommonJS for the
 * same reason as `formatterCache`: the email templates and the notification contract render on
 * the API tier.
 *
 * Four translators pick a `<key>_<category>` leaf themselves: the email templates, the
 * notification contract's server-side renderer, the strings-kit degrade path, and page-shell's
 * `defaultValue` fallback. They disagreed on the one case a catalog cannot prevent, a call with no
 * usable count. The email templates and strings-kit selected `other` (ECMA-402 answers `other`
 * for `NaN`), the notification contract returned the bare key, and page-shell looked only at a
 * `number`. The same catalog therefore rendered differently depending on which side drew it.
 *
 * The rule here is i18next's, measured from `i18next@26.2.0` (`Translator.resolve`), because
 * i18next renders the same catalogs in the browser and every other selector exists to agree with
 * it:
 *   - plural handling applies when `count !== undefined` and `count` is not a string (a string
 *     count is a caller error i18next does not coerce);
 *   - the candidates, in priority order: `<key>_zero` (only when `count === 0`), then
 *     `<key>_<category>` from `Intl.PluralRules(locale).select(count)` (which answers `other` for
 *     any non-finite number), then the bare key.
 * A missing count is therefore the bare key. For a pluralised stem that is a missing leaf, which
 * each translator surfaces its own way: the email translator throws its named unknown-key error,
 * and the notification renderer falls to its `defaultValue`. That is the caller's defect made
 * visible, not a guessed `other` that hides it.
 *
 * @module @puffin-core/i18n-bootstrap/plural-keys
 */

const { getPluralRules } = require('./formatterCache');

/** i18next's plural separator (`pluralSeparator`, default `_`). */
const PLURAL_SEPARATOR = '_';

/** The CLDR plural categories: every value `Intl.PluralRules#select` can answer. */
const PLURAL_CATEGORIES = Object.freeze(['zero', 'one', 'two', 'few', 'many', 'other']);

const PLURAL_TAIL = new RegExp(`(?:^|${PLURAL_SEPARATOR})(?:${PLURAL_CATEGORIES.join('|')})$`);

/**
 * Does this key end in a plural category, so i18next reads it as one FORM of a plural stem
 * (`kind_other` is the `other` form of `kind`)? A builder that mints keys from data asks this
 * before it emits one (LIFE-SHORTLISTED-001 U30: a site-link kind named `other`).
 *
 * @param {string} key
 * @returns {boolean}
 * @throws {TypeError} when `key` is not a string
 */
function endsInPluralCategory(key) {
  if (typeof key !== 'string') {
    throw new TypeError(`endsInPluralCategory: a key is a string, got ${typeof key}`);
  }
  return PLURAL_TAIL.test(key);
}

/**
 * Does this count put a lookup through plural resolution? i18next's own condition.
 *
 * @param {*} count
 * @returns {boolean}
 */
function needsPluralHandling(count) {
  return count !== undefined && typeof count !== 'string';
}

/**
 * The keys to try for `stem` at `count`, in i18next's priority order; the bare stem is always
 * last.
 *
 * @param {string} stem - the key as the call site spells it
 * @param {*} count - the call's `count` option
 * @param {string} locale - the Intl locale whose plural rules apply (REQUIRED)
 * @returns {string[]}
 */
function pluralCandidateKeys(stem, count, locale) {
  if (!needsPluralHandling(count)) return [stem];
  const category = getPluralRules(locale).select(count);
  const zero = count === 0 ? [`${stem}${PLURAL_SEPARATOR}zero`] : [];
  return [...zero, `${stem}${PLURAL_SEPARATOR}${category}`, stem];
}

/**
 * The first candidate the bundle holds, else the bare stem (so the caller's own unknown-key
 * handling decides what a missing leaf does).
 *
 * @param {string} stem - the key as the call site spells it
 * @param {*} count - the call's `count` option
 * @param {string} locale - the Intl locale whose plural rules apply (REQUIRED)
 * @param {(key: string) => boolean} has - does the bundle hold this key?
 * @returns {string} the key to look up
 */
function selectPluralKey(stem, count, locale, has) {
  return pluralCandidateKeys(stem, count, locale).find(has) || stem;
}

module.exports = {
  PLURAL_CATEGORIES,
  endsInPluralCategory,
  needsPluralHandling,
  pluralCandidateKeys,
  selectPluralKey,
};
